import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AuthShell } from './Login';

const schema = z.object({
  businessName: z.string().min(2, 'Business name is required'),
  name:         z.string().min(2, 'Your name is required'),
  email:        z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'At least 8 characters')
    .regex(/[A-Z]/, 'Include at least one uppercase letter')
    .regex(/[a-z]/, 'Include at least one lowercase letter')
    .regex(/\d/,    'Include at least one number'),
});

export default function Signup() {
  const { signup } = useAuth();
  const navigate   = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [apiErr, setApiErr] = useState('');

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  async function onSubmit(data) {
    setApiErr('');
    try {
      await signup(data);
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setApiErr(err.response?.data?.error?.message || 'Signup failed. Please try again.');
    }
  }

  return (
    <AuthShell title="Create your account" subtitle="Start your 14-day free trial — no credit card required">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {apiErr && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{apiErr}</span>
          </div>
        )}

        <div>
          <label className="label">Business name</label>
          <input {...register('businessName')} placeholder="Art of Threading" className={`input ${errors.businessName ? 'border-red-400' : ''}`} />
          {errors.businessName && <p className="mt-1 text-xs text-red-500">{errors.businessName.message}</p>}
        </div>

        <div>
          <label className="label">Your name</label>
          <input {...register('name')} placeholder="Jane Smith" className={`input ${errors.name ? 'border-red-400' : ''}`} />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>

        <div>
          <label className="label">Work email</label>
          <input {...register('email')} type="email" placeholder="jane@business.com" className={`input ${errors.email ? 'border-red-400' : ''}`} />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        <div>
          <label className="label">Password</label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPw ? 'text' : 'password'}
              placeholder="Min 8 chars, upper, lower, number"
              className={`input pr-10 ${errors.password ? 'border-red-400' : ''}`}
            />
            <button type="button" onClick={() => setShowPw(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full justify-center py-2.5 mt-2">
          {isSubmitting ? 'Creating account…' : (
            <><span>Create account</span><ArrowRight size={16} /></>
          )}
        </button>

        <p className="text-center text-xs text-ink-muted">
          By signing up you agree to our Terms of Service and Privacy Policy.
        </p>
      </form>

      <p className="text-center text-sm text-ink-secondary mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
      </p>
    </AuthShell>
  );
}
